import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable, BehaviorSubject } from 'rxjs';

@Injectable()
export class CountriesService {
  list: any;
  obs: Observable<any>;
  public langEquivalences = {
    en: 'eng',
    de: 'deu',
    fr: 'fra',
    hr: 'hrv',
    it: 'ita',
    jp: 'jpn',
    nl: 'nld',
    pt: 'por',
    ru: 'rus',
    sl: 'slk',
    es: 'spa',
    fn: 'fin',
    zh: 'zho'
  };
  public countriesOptionsSubject = new BehaviorSubject({});

  constructor(public http: HttpClient, public translate: TranslateService) {
    translate.onLangChange.subscribe(() => {
      this.countriesOptionsSubject.next({ options: this.getOptions() });
    });
  }

  public getCountries(): Observable<any> | any {
    if (!this.list) {
      this.obs = this.http.get('public/data/countries.json').pipe(
        map((list: any[]) => {
          this.list = list;
          this.countriesOptionsSubject.next({ options: this.getOptions() });
          return this.list;
        })
      );
      return this.obs;
    }
    return this.list;
  }

  public getOptions(): Array<any> {
    if (!this.list) {
      return null;
    }
    const globalList = this.list
      .map((country) => ({
        key: country.cca3,
        value: this.getLocalCountryName(country)
      }))
      .sort((a, b) => a.value.localeCompare(b.value));

    const mainCountriesCCA3 = ['ESP', 'FRA', 'MEX', 'USA'];
    const prefixCountries = globalList.filter((country) => mainCountriesCCA3.indexOf(country.key) > -1);

    const groups = [
      { section: this.translate.instant('properties.favorites'), options: prefixCountries },
      { section: this.translate.instant('properties.others'), options: globalList }
    ];
    return groups;
  }

  getOptionsObservable(): Observable<any> {
    return this.countriesOptionsSubject.asObservable();
  }

  getTimezoneOptions(countryCCA3) {
    const country = this.list.find((item) => item.cca3 === countryCCA3);
    if (!country) {
      return [];
    }
    return country.timezones.map((timezone) => ({ key: timezone, value: timezone }));
  }

  getCountry(countryCCA3) {
    return this.list.find((country) => country.cca3 === countryCCA3);
  }

  getCountryName(countryCCA3) {
    if (!countryCCA3) {
      return '';
    }
    const country = this.getCountry(countryCCA3);
    return this.getLocalCountryName(country);
  }

  getLocalCountryName(country) {
    if (!country) return '';
    const lang = this.langEquivalences[this.translate.currentLang || this.translate.getDefaultLang()];
    return country.translations[lang] ? country.translations[lang].common : country.name.common;
  }

  getDefaultTimezone(country) {
    const defaults = {
      CHL: 'America/Santiago',
      DEU: 'Europe/Berlin',
      ESP: 'Europe/Madrid',
      FRA: 'Europe/Paris',
      MEX: 'America/Mexico_City',
      PRT: 'Europe/Lisbon',
      USA: 'America/New_York'
    };
    if (defaults[country]) {
      return defaults[country];
    }
    const countryTimezones = this.getTimezoneOptions(country);
    if (countryTimezones && countryTimezones.length) {
      return countryTimezones[0].key;
    }
    return null;
  }
}
